import React from "react";
import { Package, Calendar, FileText, XCircle, Trash2 } from "lucide-react";
import {
  AmendPurchaseOrderFormAction,
  AmendPurchaseOrderFormData,
  AmendPurchaseOrderFormDataKey,
  ProductAmendmentInputField,
  PurchaseOrder
} from "src/types";
import { ValidationErrors } from "src/errors";
import { useScrollOnMessage } from "src/hooks";
import { OrderProductAmendmentInputTable } from "src/components/tables/inputTables";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { DateInputBox, TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { SubmissionTooltip } from "src/components/tooltips";

interface AmendPurchaseOrderFormProps {
  formData: AmendPurchaseOrderFormData;
  purchaseOrderToAmend?: PurchaseOrder;
  validationErrors?: ValidationErrors<AmendPurchaseOrderFormDataKey>;
  formLvlErrorMessage: string;
  isFormComplete: boolean;
  isCancelOnlyMode: boolean;
  handleInputChange: (
    field: AmendPurchaseOrderFormDataKey,
    value: AmendPurchaseOrderFormData[keyof AmendPurchaseOrderFormData]
  ) => void;
  handleSubmit: (e?: React.FormEvent) => void;
  setAction: (action: AmendPurchaseOrderFormAction) => void;
}

const AmendPurchaseOrderForm: React.FC<AmendPurchaseOrderFormProps> = ({
  formData,
  purchaseOrderToAmend,
  validationErrors,
  formLvlErrorMessage,
  isFormComplete,
  isCancelOnlyMode,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const handleSetProductAmendments: React.Dispatch<React.SetStateAction<ProductAmendmentInputField[]>> = (
    updatedProductAmendments
  ) => {
    handleInputChange("productAmendments", updatedProductAmendments as ProductAmendmentInputField[]);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Amend Purchase Order</h1>
            <p className="text-gray-300 mt-2 text-lg">Modify purchase order details and product quantities</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Search Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Purchase Order Search
                </h3>
                <TextInputBox
                  id="purchase-order-id"
                  placeholder="Purchase Order ID"
                  onChange={(value) => {
                    handleInputChange("purchaseOrderId", value.toUpperCase());
                  }}
                  value={formData.purchaseOrderId}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("purchaseOrderId")} />
              </div>

              {purchaseOrderToAmend && !isCancelOnlyMode && (
                <>
                  {/* Product Amendments Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Product Amendments
                    </h3>
                    <OrderProductAmendmentInputTable
                      productAmendments={formData.productAmendments}
                      setProductAmendments={handleSetProductAmendments}
                      productConfirmations={purchaseOrderToAmend?.confirmationDetails?.products}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("productAmendments")}
                    />
                  </div>

                  {/* Estimated Delivery Date Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      Delivery Date
                    </h3>
                    <DateInputBox
                      id="estimated-delivery-date"
                      label="Estimated Delivery Date"
                      onChange={(value) => handleInputChange("estDeliveryDate", value)}
                      value={formData.estDeliveryDate}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("estDeliveryDate")}
                    />
                  </div>
                </>
              )}

              {purchaseOrderToAmend && isCancelOnlyMode && (
                <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                  <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Purchase order confirmed
                  </h3>
                  <p className="text-sm text-gray-600">
                    This purchase order has already been confirmed by the vendor. Quantity and delivery date amendments
                    are locked, but you can still cancel the order using the section below.
                  </p>
                </div>
              )}

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Submit Button */}
              <div className="flex items-center gap-4">
                {!purchaseOrderToAmend && (
                  <button
                    className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                    disabled={!isFormComplete}
                    onClick={() => setAction("FETCH")}
                    type="submit"
                  >
                    Search
                  </button>
                )}

                {purchaseOrderToAmend && !isCancelOnlyMode && (
                  <>
                    <button
                      className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                      disabled={!isFormComplete}
                      onClick={() => setAction("AMEND")}
                      type="submit"
                    >
                      Save Amendments
                    </button>

                    {!isFormComplete && <SubmissionTooltip formState="NO_CHANGE" />}
                  </>
                )}
              </div>

              {/* Delete Section - Only for Draft and Scheduled Orders */}
              {(purchaseOrderToAmend?.completionStatus === "Draft" ||
                purchaseOrderToAmend?.completionStatus === "Scheduled") && (
                <div className="border border-warning-200 rounded-2xl p-5 bg-warning-50">
                  <h3 className="text-sm font-semibold text-warning-800 mb-4 tracking-wide uppercase flex items-center">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {purchaseOrderToAmend.completionStatus} Purchase Order
                  </h3>
                  <p className="text-sm text-warning-700 mb-4">
                    This purchase order is in {purchaseOrderToAmend.completionStatus.toLowerCase()} status and can be
                    permanently deleted.
                  </p>
                  <button
                    className="px-6 py-3 bg-warning-600 text-white rounded-lg hover:bg-warning-700 transition-colors text-sm font-medium"
                    onClick={() => setAction("DELETE_CONFIRMATION")}
                    type="submit"
                  >
                    Delete Purchase Order
                  </button>
                </div>
              )}

              {/* Cancellation Section - Only for In Progress Orders */}
              {purchaseOrderToAmend?.completionStatus === "In Progress" && (
                <div className="border border-error-200 rounded-2xl p-5 bg-error-50">
                  <h3 className="text-sm font-semibold text-error-800 mb-4 tracking-wide uppercase flex items-center">
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Purchase Order
                  </h3>
                  <TextInputBoxLarge
                    id="cancellation-reason"
                    placeholder="Cancellation Reason"
                    onChange={(value) => handleInputChange("cancellationReason", value)}
                    value={formData.cancellationReason ?? ""}
                    maxLength={650}
                  />
                  <ValidationErrorMessage
                    message={validationErrors?.getErrorMessageByFormDataKey("cancellationReason")}
                  />
                  <div className="mt-4">
                    <button
                      className="px-6 py-3 bg-error-600 text-white rounded-lg hover:bg-error-700 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                      disabled={!formData.cancellationReason}
                      onClick={() => setAction("CANCEL_CONFIRMATION")}
                      type="submit"
                    >
                      Cancel Purchase Order
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { AmendPurchaseOrderForm };
